import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tooltip,
  Tabs,
  Tab,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getCurrentEnvironment } from '../config/environment';



const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '8px 16px',
  marginRight: theme.spacing(1),
}));

// Types
interface Role {
  id: string;
  name: string;
  display_name: string;
  description: string;
  is_system_role: boolean;
  is_default: boolean;
  permissions: string[];
  created_at: string;
}

interface Permission {
  id: string;
  name: string;
  display_name: string;
  description: string;
  module: string;
  action: string;
  resource: string;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const RolesPermissionsEditor: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);


  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Role dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    is_system_role: false,
    is_default: false,
    permissions: [] as string[]
  });

  // Permission dialog state
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [permissionForm, setPermissionForm] = useState({
    name: '',
    display_name: '',
    description: '',
    module: '',
    action: '',
    resource: ''
  });

  const API_BASE = getCurrentEnvironment().aletheiaAdminApi;

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {

    try {
      // Load roles
      const rolesResponse = await fetch(`${API_BASE}/api/admin/roles`);
      if (rolesResponse.ok) {
        const rolesData = await rolesResponse.json();
        setRoles(rolesData.roles || []);
      }

      // Load permissions
      const permissionsResponse = await fetch(`${API_BASE}/api/admin/permissions`);
      if (permissionsResponse.ok) {
        const permissionsData = await permissionsResponse.json();
        setPermissions(permissionsData.permissions || []);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    }
  };

  // Role management functions
  const openRoleDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        display_name: role.display_name,
        description: role.description,
        is_system_role: role.is_system_role,
        is_default: role.is_default,
        permissions: role.permissions
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        name: '',
        display_name: '',
        description: '',
        is_system_role: false,
        is_default: false,
        permissions: []
      });
    }
    setRoleDialogOpen(true);
  };

  const closeRoleDialog = () => {
    setRoleDialogOpen(false);
    setEditingRole(null);
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      is_system_role: false,
      is_default: false,
      permissions: []
    });
  };

  const handleCreateRole = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(roleForm),
      });

      if (response.ok) {
        setSuccess('Role created successfully');
        closeRoleDialog();
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create role');
      }
    } catch (err) {
      setError('Failed to create role');
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/roles/${editingRole.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(roleForm),
      });

      if (response.ok) {
        setSuccess('Role updated successfully');
        closeRoleDialog();
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update role');
      }
    } catch (err) {
      setError('Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/roles/${roleId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccess('Role deleted successfully');
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete role');
      }
    } catch (err) {
      setError('Failed to delete role');
    }
  };

  // Permission management functions
  const openPermissionDialog = (permission?: Permission) => {
    if (permission) {
      setEditingPermission(permission);
      setPermissionForm({
        name: permission.name,
        display_name: permission.display_name,
        description: permission.description,
        module: permission.module,
        action: permission.action,
        resource: permission.resource
      });
    } else {
      setEditingPermission(null);
      setPermissionForm({
        name: '',
        display_name: '',
        description: '',
        module: '',
        action: '',
        resource: ''
      });
    }
    setPermissionDialogOpen(true);
  };

  const closePermissionDialog = () => {
    setPermissionDialogOpen(false);
    setEditingPermission(null);
    setPermissionForm({
      name: '',
      display_name: '',
      description: '',
      module: '',
      action: '',
      resource: ''
    });
  };

  const handleCreatePermission = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(permissionForm),
      });

      if (response.ok) {
        setSuccess('Permission created successfully');
        closePermissionDialog();
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create permission');
      }
    } catch (err) {
      setError('Failed to create permission');
    }
  };

  const handleUpdatePermission = async () => {
    if (!editingPermission) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/permissions/${editingPermission.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(permissionForm),
      });

      if (response.ok) {
        setSuccess('Permission updated successfully');
        closePermissionDialog();
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update permission');
      }
    } catch (err) {
      setError('Failed to update permission');
    }
  };

  const handleDeletePermission = async (permissionId: string) => {
    if (!window.confirm('Are you sure you want to delete this permission?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/permissions/${permissionId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccess('Permission deleted successfully');
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete permission');
      }
    } catch (err) {
      setError('Failed to delete permission');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.module]) {
      acc[permission.module] = [];
    }
    acc[permission.module].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Roles & Permissions Editor
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Roles" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Permissions" icon={<SecurityIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Roles Tab */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Roles Management</Typography>
          <ActionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openRoleDialog()}
          >
            Add Role
          </ActionButton>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {role.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{role.display_name}</TableCell>
                  <TableCell>{role.description}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {role.permissions.slice(0, 3).map((permission) => (
                        <Chip key={permission} label={permission} size="small" />
                      ))}
                      {role.permissions.length > 3 && (
                        <Chip label={`+${role.permissions.length - 3} more`} size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={role.is_system_role ? 'System' : 'Custom'}
                      size="small"
                      color={role.is_system_role ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit Role">
                        <IconButton size="small" onClick={() => openRoleDialog(role)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {!role.is_system_role && (
                        <Tooltip title="Delete Role">
                          <IconButton size="small" onClick={() => handleDeleteRole(role.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Permissions Tab */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Permissions Management</Typography>
          <ActionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openPermissionDialog()}
          >
            Add Permission
          </ActionButton>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map((permission) => (
                <TableRow key={permission.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {permission.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{permission.display_name}</TableCell>
                  <TableCell>{permission.module}</TableCell>
                  <TableCell>{permission.action}</TableCell>
                  <TableCell>{permission.resource}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit Permission">
                        <IconButton size="small" onClick={() => openPermissionDialog(permission)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Permission">
                        <IconButton size="small" onClick={() => handleDeletePermission(permission.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={closeRoleDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Role Name"
              value={roleForm.name}
              onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
              disabled={!!editingRole} // Can't change name when editing
              fullWidth
            />
            <TextField
              label="Display Name"
              value={roleForm.display_name}
              onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={roleForm.description}
              onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={roleForm.is_system_role}
                  onChange={(e) => setRoleForm({ ...roleForm, is_system_role: e.target.checked })}
                  disabled={!!editingRole}
                />
              }
              label="System Role"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={roleForm.is_default}
                  onChange={(e) => setRoleForm({ ...roleForm, is_default: e.target.checked })}
                />
              }
              label="Default Role"
            />
            
            <Typography variant="h6" sx={{ mt: 2 }}>
              Permissions
            </Typography>
            
            {Object.entries(groupedPermissions).map(([module, modulePermissions]) => (
              <Accordion key={module}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={500}>
                    {module} ({modulePermissions.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {modulePermissions.map((permission) => (
                      <ListItem key={permission.id} dense>
                        <ListItemIcon>
                          <Checkbox
                            checked={roleForm.permissions.includes(permission.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRoleForm({
                                  ...roleForm,
                                  permissions: [...roleForm.permissions, permission.name]
                                });
                              } else {
                                setRoleForm({
                                  ...roleForm,
                                  permissions: roleForm.permissions.filter(p => p !== permission.name)
                                });
                              }
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={permission.display_name}
                          secondary={permission.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRoleDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={editingRole ? handleUpdateRole : handleCreateRole}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Dialog */}
      <Dialog open={permissionDialogOpen} onClose={closePermissionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPermission ? 'Edit Permission' : 'Create New Permission'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Permission Name"
              value={permissionForm.name}
              onChange={(e) => setPermissionForm({ ...permissionForm, name: e.target.value })}
              disabled={!!editingPermission}
              fullWidth
            />
            <TextField
              label="Display Name"
              value={permissionForm.display_name}
              onChange={(e) => setPermissionForm({ ...permissionForm, display_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={permissionForm.description}
              onChange={(e) => setPermissionForm({ ...permissionForm, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="Module"
              value={permissionForm.module}
              onChange={(e) => setPermissionForm({ ...permissionForm, module: e.target.value })}
              fullWidth
            />
            <TextField
              label="Action"
              value={permissionForm.action}
              onChange={(e) => setPermissionForm({ ...permissionForm, action: e.target.value })}
              fullWidth
            />
            <TextField
              label="Resource"
              value={permissionForm.resource}
              onChange={(e) => setPermissionForm({ ...permissionForm, resource: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePermissionDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={editingPermission ? handleUpdatePermission : handleCreatePermission}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            {editingPermission ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesPermissionsEditor; 